import * as React from 'react'

import { Button, ButtonProps } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useIsMd } from '@/hooks'
import { CommonChevronDown } from '@/icons'
import { cn } from '@/lib/utils'

import RenderIf from './render-if'
import { Text } from './text'

const PaginationWrapper = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto flex w-full justify-between', className)}
    {...props}
  />
)
PaginationWrapper.displayName = 'PaginationWrapper'

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  )
)
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('list-none', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>

type PaginationButtonProps = {
  isActive?: boolean
} & ButtonProps

const PaginationLink = ({ className, isActive, ...props }: PaginationLinkProps) => (
  <a aria-current={isActive ? 'page' : undefined} className={cn(className)} {...props} />
)
const PaginationButton = ({ className, isActive, ...props }: PaginationButtonProps) => {
  return (
    <Button
      aria-current={isActive ? 'page' : undefined}
      variant='icon'
      className={cn('w-8 h-8', isActive ? 'bg-white-4' : '', className)}
      {...props}
    />
  )
}
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({ className, ...props }: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton aria-label='Go to previous page' size='default' className={className} {...props}>
    <CommonChevronDown className='size-6 rotate-90' />
  </PaginationButton>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationButton>) => (
  <PaginationButton aria-label='Go to next page' size='default' className={className} {...props}>
    <CommonChevronDown className='size-6 rotate-[-90deg]' />
  </PaginationButton>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span aria-label='More pages' className={cn('text-size-14 w-8 text-center', className)} {...props}>
    ...
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

interface PageSizeProps {
  pageSizeOptions: number[]
  pageSize: number
  onPageSizeChange?: (size: number) => void
}
const PageSize: React.FC<PageSizeProps> = ({ pageSizeOptions, pageSize, onPageSizeChange }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className='trigger'>
        <Button variant='secondary' size='sm' className='px-3 flex gap-1'>
          {pageSize} <CommonChevronDown className={cn('transition-all duration-300 size-6')} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='flex flex-col gap-1 w-24' onCloseAutoFocus={(e) => e.preventDefault()}>
        {pageSizeOptions.map((size) => (
          <DropdownMenuItem
            onClick={() => onPageSizeChange?.(size)}
            key={size}
            className={cn(
              'cursor-pointer justify-center focus-visible:outline-none focus-visible:ring-0',
              size === pageSize ? 'bg-white-4' : ''
            )}
          >
            {size}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface PaginationProps {
  page: number
  pageSize?: number
  total: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]
  className?: string
  maxPagesWithoutEllipsis?: number
  pageContentClassName?: string
  hideItemsPerPage?: boolean
}

const Pagination = ({
  page,
  pageSize = 20,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  className,
  maxPagesWithoutEllipsis = 6,
  pageContentClassName,

  ...props
}: PaginationProps) => {
  const isMd = useIsMd()
  const totalPages = Math.ceil(total / pageSize)
  const isFirstPage = page === 1
  const isLastPage = page === totalPages

  const range = (start: number, end: number) => {
    const length = end - start + 1
    return Array.from({ length }, (_, idx) => idx + start)
  }

  const generatePaginationRange = () => {
    // If total pages is less than or equal to 7, show all pages
    if (totalPages <= maxPagesWithoutEllipsis) return range(1, totalPages)

    // For total pages > 8, we need to show exactly 7 elements (excluding prev/next)
    // Case 1: Current page is near start (pages 1-4)
    if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages]

    // Case 2: Current page is near end (last 4 pages)
    if (page >= totalPages - 3)
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]

    // Case 3: Current page is in the middle
    return [1, '...', page - 1, page, page + 1, '...', totalPages]
  }

  const paginationRange = generatePaginationRange()

  return (
    <PaginationWrapper {...props}>
      {/* Left Section */}
      <RenderIf condition={isMd}>
        <div className='flex items-center gap-2'>
          {/* <RenderIf condition={!hideItemsPerPage}>
            <Text as='span' color='white-48'>
              Items per page
            </Text>
          </RenderIf> */}
          <PageSize pageSizeOptions={pageSizeOptions} pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
          <Text as='span' color='white-48'>
            of {total} items
          </Text>
        </div>
      </RenderIf>

      {/* Right Section - Always exactly 9 elements total */}
      <div className={cn('flex items-center space-x-3', pageContentClassName)}>
        <PaginationPrevious
          onClick={() => !isFirstPage && onPageChange?.(page - 1)}
          className={cn(isFirstPage && 'pointer-events-none text-white-12')}
        />

        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return <PaginationEllipsis key={`ellipsis-${index}`} />
          }

          return (
            <PaginationItem key={pageNumber}>
              <PaginationButton isActive={page === pageNumber} onClick={() => onPageChange?.(pageNumber as number)}>
                {pageNumber}
              </PaginationButton>
            </PaginationItem>
          )
        })}

        <PaginationNext
          onClick={() => !isLastPage && onPageChange?.(page + 1)}
          className={cn(isLastPage && 'pointer-events-none text-white-12')}
        />
      </div>
    </PaginationWrapper>
  )
}

Pagination.displayName = 'Pagination'

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
}
